"use client";

import { usePathname } from "next/navigation";
import RatingPrompt from "@/components/RatingPrompt";

/**
 * "Was this helpful?" feedback block for editorial / article SEO content pages
 * (festival, blog, nakshatra/info, horoscope explainer bodies) — the pages that
 * are NOT calculators/tools (those mount RatingPrompt inline themselves).
 *
 * Mounts ONE shared RatingPrompt with:
 *   - source="content_page"  (the backend VALID_SOURCES string for these pages —
 *     added in parallel by the backend feedback agent; use EXACTLY this string)
 *   - mode="thumbs+text"     (a 👍/👎 with an optional free-text follow-up)
 *   - context={{ path }}     (the route path, so feedback is sliceable per page)
 *
 * `source="content_page"` is style:inline in the prompt-ceiling table, so it is
 * always allowed and never counts against the per-session modal ceiling — but it
 * still records dedupe per page (path) so a seeker isn't re-asked on the same URL.
 *
 * Designed to be dropped once into a shared editorial body/layout so every
 * content page is covered without per-page wiring.
 */
export default function ContentPageFeedback({
  path: pathProp,
  prompt = "Was this helpful?",
  className = "",
}: {
  /** Override the route path (e.g. server-known canonical path). Defaults to the live pathname. */
  path?: string;
  prompt?: string;
  className?: string;
}) {
  const pathname = usePathname();
  const path = pathProp ?? pathname ?? undefined;

  return (
    <div
      className={`my-6 rounded-xl p-4 ${className}`}
      style={{
        background: "var(--color-bg-warm)",
        border: "1px solid var(--ish-border)",
      }}
    >
      <RatingPrompt
        source="content_page"
        mode="thumbs+text"
        prompt={prompt}
        context={{ path }}
        dedupeKey={path ? `content_page:${path}` : undefined}
      />
    </div>
  );
}
